import type { ReactNode } from 'react';
import { Button, cn } from '@cyphex/ui';

export interface SettingRowProps {
  label: string;
  hint?: string;
  /** Shown in warning tone under the hint — e.g. why an effect degraded. */
  notice?: string | null;
  children: ReactNode;
  className?: string;
}

/** One labelled setting: description on the left, control on the right. */
export function SettingRow({ label, hint, notice, children, className }: SettingRowProps) {
  return (
    <div className={cn('flex items-start justify-between gap-4 py-2', className)}>
      <div className="min-w-0">
        <p className="text-md font-medium">{label}</p>
        {hint ? <p className="mt-0.5 text-sm text-muted-foreground">{hint}</p> : null}
        {notice ? <p className="mt-0.5 text-sm text-warning">{notice}</p> : null}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

export interface ToggleRowProps {
  label: string;
  hint?: string;
  notice?: string | null;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}

/** A setting with an on/off control. `aria-pressed` carries the state. */
export function ToggleRow({
  label, hint, notice, checked, disabled = false, onChange,
}: ToggleRowProps) {
  return (
    <SettingRow label={label} hint={hint} notice={notice}>
      <Button
        variant={checked ? 'subtle' : 'outline-neutral'}
        size="sm"
        aria-pressed={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
      >
        {checked ? 'On' : 'Off'}
      </Button>
    </SettingRow>
  );
}
